import { confirmDialog } from '../shared/rc-confirm-dialog';
import { documentsApi, dossiersApi, maintenanceActivitiesApi, remindersApi, rentalAgreementsApi, tenantsApi, type CreateDocumentRequest, type CreateMaintenanceActivityRequest, type CreateReminderRequest, type CreateRentalAgreementRequest, type CreateTenantRequest, type ExportPropertyDossierRequest, type PropertyDto, type RentalAgreementDto, type TenantDto, type UpdateRentalAgreementRequest } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { t, translateDocumentType, translateMaintenanceActivityCategory } from '../../services/i18n';
import { PROPERTY_DOC_TYPES, PROPERTY_ICONS, PROPERTY_MAINTENANCE_CATEGORIES, escapeHtml, formatDate } from './property-shared';
import { openPropertyForm } from './property-list-dialogs';

function formatMonthlyRent(amount?: number): string {
  return amount != null ? `&euro;${amount.toFixed(2)}${t('property.perMonth')}` : '-';
}

function openOverlay(html: string, zIndex?: string): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  if (zIndex) overlay.style.zIndex = zIndex;
  overlay.innerHTML = html;
  document.body.appendChild(overlay);
  return overlay;
}

function clearDialogError(root: ParentNode): void {
  const errorBox = root.querySelector<HTMLElement>('[data-form-error]');
  if (!errorBox) {
    return;
  }

  errorBox.hidden = true;
  errorBox.textContent = '';
}

function showDialogError(root: ParentNode, message: string): void {
  const errorBox = root.querySelector<HTMLElement>('[data-form-error]');
  if (!errorBox) {
    return;
  }

  errorBox.hidden = false;
  errorBox.textContent = message;
}

export function openEditPropertyDialog(property: PropertyDto, onSaved: () => Promise<void>): void {
  openPropertyForm({ property, onSaved });
}

export function openNewTenantDialog(propertyId: string, onSaved: () => Promise<void>): void {
  const overlay = openOverlay(`
    <div class="modal">
      <div class="modal-header"><h2>${t('tenant.form.addTitle')}</h2><button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button></div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-row"><div class="form-group"><label>${t('common.name')} *</label><input name="firstName" required></div><div class="form-group"><label>${t('tenant.form.lastName')} *</label><input name="lastName" required></div></div>
        <div class="form-row"><div class="form-group"><label>${t('common.email')}</label><input name="email" type="email"></div><div class="form-group"><label>${t('common.phone')}</label><input name="phone"></div></div>
        <div class="form-group"><label>${t('common.rentAmountPerMonth')} *</label><input name="rentAmount" type="number" step="0.01" min="0" required></div>
        <div class="form-row"><div class="form-group"><label>${t('common.startDate')} *</label><input name="startDate" type="date" required></div><div class="form-group"><label>${t('common.endDate')}</label><input name="endDate" type="date"></div></div>
        <div class="modal-actions"><button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button><button type="submit" class="btn btn-primary">${t('common.add')}</button></div>
      </form>
    </div>
  `);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    clearDialogError(overlay);
    submitBtn.disabled = true;
    const fd = new FormData(event.target as HTMLFormElement);
    const data: CreateTenantRequest = {
      propertyId,
      firstName: fd.get('firstName') as string,
      lastName: fd.get('lastName') as string,
      email: (fd.get('email') as string) || undefined,
      phone: (fd.get('phone') as string) || undefined,
      rentAmount: parseFloat(fd.get('rentAmount') as string),
      startDate: fd.get('startDate') as string,
      endDate: (fd.get('endDate') as string) || undefined,
    };
    let createdTenant: TenantDto | null = null;
    try {
      createdTenant = await tenantsApi.create(data);
      await rentalAgreementsApi.create({ tenantId: createdTenant.id, propertyId, rentAmount: data.rentAmount, startDate: data.startDate, endDate: data.endDate });
      close();
      await onSaved();
    } catch (err) {
      if (createdTenant) {
        try { await tenantsApi.delete(createdTenant.id); } catch (rollbackErr) { console.error('Failed to roll back tenant after agreement creation error:', rollbackErr); }
      }
      console.error('Failed to create tenant:', err);
      showDialogError(overlay, err instanceof ApiError ? err.message : t('property.form.addTenantError'));
      submitBtn.disabled = false;
    }
  });
}

export async function openLinkTenantDialog(propertyId: string, linkedAgreements: RentalAgreementDto[], onSaved: () => Promise<void>): Promise<void> {
  let allTenants: TenantDto[] = [];
  try {
    allTenants = await tenantsApi.getAll();
  } catch (e) {
    console.error('Failed to load tenants:', e);
    return;
  }
  const linkedIds = new Set(linkedAgreements.map((agreement) => agreement.tenantId));
  const available = allTenants.filter((tenant) => !linkedIds.has(tenant.id));
  const overlay = openOverlay(`
    <div class="modal">
      <div class="modal-header"><h2>${t('property.form.linkTenantTitle')}</h2><button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button></div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        ${available.length === 0
          ? `<p class="text-muted">${t('property.form.noAvailableTenants')}</p>`
          : `<div class="form-group"><label>${t('common.tenant')} *</label><select name="tenantId" required>${available.map((tenant) => `<option value="${tenant.id}">${escapeHtml(tenant.firstName)} ${escapeHtml(tenant.lastName)}</option>`).join('')}</select></div>
              <div class="form-group"><label>${t('common.rentAmountPerMonth')} *</label><input name="rentAmount" type="number" step="0.01" min="0" required></div>
              <div class="form-row"><div class="form-group"><label>${t('common.startDate')} *</label><input name="startDate" type="date" required></div><div class="form-group"><label>${t('common.endDate')}</label><input name="endDate" type="date"></div></div>`}
        <div class="modal-actions">
          <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
          ${available.length > 0 ? `<button type="submit" class="btn btn-primary">${t('property.form.linkTenantButton')}</button>` : ''}
        </div>
      </form>
    </div>
  `);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    clearDialogError(overlay);
    submitBtn.disabled = true;
    const fd = new FormData(event.target as HTMLFormElement);
    const data: CreateRentalAgreementRequest = {
      tenantId: fd.get('tenantId') as string,
      propertyId,
      rentAmount: parseFloat(fd.get('rentAmount') as string),
      startDate: fd.get('startDate') as string,
      endDate: (fd.get('endDate') as string) || undefined,
    };
    try {
      await rentalAgreementsApi.create(data);
      close();
      await onSaved();
    } catch (err) {
      console.error('Failed to link tenant:', err);
      showDialogError(overlay, err instanceof ApiError ? err.message : t('property.form.linkTenantError'));
      submitBtn.disabled = false;
    }
  });
}

export function openDocumentDialog(propertyId: string, onSaved: () => Promise<void>): void {
  const overlay = openOverlay(`
    <div class="modal">
      <div class="modal-header"><h2>${t('document.upload')}</h2><button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button></div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group"><label>${t('common.file')} *</label><input name="file" type="file" accept=".pdf,.jpg,.jpeg,.png" required></div>
        <div class="form-group"><label>${t('property.form.documentTypeLabel')} *</label><select name="documentType" required>${PROPERTY_DOC_TYPES.map((type) => `<option value="${type}">${escapeHtml(translateDocumentType(type))}</option>`).join('')}</select></div>
        <div class="form-group"><label>${t('common.expiryDate')}</label><input name="expiryDate" type="date"></div>
        <div class="form-group"><label>${t('common.tags')}</label><input name="tags" placeholder="${t('document.tagsPlaceholder')}"></div>
        <div class="modal-actions"><button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button><button type="submit" class="btn btn-primary">${t('common.upload')}</button></div>
      </form>
    </div>
  `);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    submitBtn.disabled = true;
    submitBtn.textContent = t('common.uploading');
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    const fd = new FormData(event.target as HTMLFormElement);
    const file = fd.get('file') as File;
    if (!file) return;
    const data: CreateDocumentRequest = {
      propertyId,
      fileName: file.name,
      contentType: file.type,
      fileSizeBytes: file.size,
      documentType: fd.get('documentType') as string,
      expiryDate: (fd.get('expiryDate') as string) || undefined,
      tags: (fd.get('tags') as string) || undefined,
    };
    try {
      const result = await documentsApi.create(data);
      const uploadResponse = await fetch(result.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!uploadResponse.ok) {
        await documentsApi.delete(result.documentId).catch(() => undefined);
        throw new Error(t('document.uploadError'));
      }
      close();
      await onSaved();
    } catch (err) {
      console.error('Failed to upload document:', err);
      if (errorBox) {
        errorBox.textContent = err instanceof ApiError && err.code === 'DOCUMENT_STORAGE_LIMIT_EXCEEDED'
          ? err.message
          : (err instanceof Error ? err.message : t('document.uploadError'));
        errorBox.hidden = false;
      }
      submitBtn.disabled = false;
      submitBtn.textContent = t('common.upload');
    }
  });
}

export function openReminderDialog(propertyId: string, onSaved: () => Promise<void>): void {
  const overlay = openOverlay(`
    <div class="modal">
      <div class="modal-header"><h2>${t('reminder.add')}</h2><button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button></div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group"><label>${t('common.title')} *</label><input name="title" required placeholder="${t('reminder.titlePlaceholder')}"></div>
        <div class="form-group"><label>${t('common.deadline')} *</label><input name="dueDate" type="date" required></div>
        <div class="modal-actions"><button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button><button type="submit" class="btn btn-primary">${t('common.add')}</button></div>
      </form>
    </div>
  `);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    clearDialogError(overlay);
    submitBtn.disabled = true;
    const fd = new FormData(event.target as HTMLFormElement);
    const data: CreateReminderRequest = { propertyId, title: fd.get('title') as string, dueDate: fd.get('dueDate') as string };
    try {
      await remindersApi.create(data);
      close();
      await onSaved();
    } catch (err) {
      console.error('Failed to create reminder:', err);
      showDialogError(overlay, err instanceof ApiError ? err.message : t('property.form.reminderError'));
      submitBtn.disabled = false;
    }
  });
}

export function openMaintenanceActivityDialog(propertyId: string, onSaved: () => Promise<void>): void {
  const overlay = openOverlay(`
    <div class="modal">
      <div class="modal-header"><h2>${t('maintenanceActivities.add')}</h2><button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button></div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-group"><label>${t('common.date')} *</label><input name="activityDate" type="date" required></div>
        <div class="form-group"><label>${t('common.description')} *</label><textarea name="description" rows="4" required placeholder="${t('maintenanceActivities.descriptionPlaceholder')}"></textarea></div>
        <div class="form-row">
          <div class="form-group"><label>${t('common.category')} *</label><select name="category" required>${PROPERTY_MAINTENANCE_CATEGORIES.map((category) => `<option value="${category}">${escapeHtml(translateMaintenanceActivityCategory(category))}</option>`).join('')}</select></div>
          <div class="form-group"><label>${t('maintenanceActivities.amountOptional')}</label><input name="amount" type="number" step="0.01" min="0" placeholder="0.00"></div>
        </div>
        <div class="form-group"><label>${t('maintenanceActivities.attachmentOptional')}</label><input name="attachment" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"></div>
        <div class="modal-actions"><button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button><button type="submit" class="btn btn-primary">${t('common.save')}</button></div>
      </form>
    </div>
  `);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = overlay.querySelector<HTMLElement>('[data-form-error]');
    submitBtn.disabled = true;
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    const fd = new FormData(event.target as HTMLFormElement);
    const file = fd.get('attachment') as File | null;
    const requestData: CreateMaintenanceActivityRequest = {
      activityDate: fd.get('activityDate') as string,
      description: fd.get('description') as string,
      category: fd.get('category') as string,
      amount: (fd.get('amount') as string) ? parseFloat(fd.get('amount') as string) : undefined,
      attachmentFileName: file?.size ? file.name : undefined,
      attachmentContentType: file?.size ? (file.type || 'application/octet-stream') : undefined,
      attachmentFileSizeBytes: file?.size ? file.size : undefined,
    };
    try {
      const result = await maintenanceActivitiesApi.create(propertyId, requestData);
      if (file?.size && result.attachmentUploadUrl) {
        const uploadResponse = await fetch(result.attachmentUploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!uploadResponse.ok) {
          await maintenanceActivitiesApi.delete(result.activity.id).catch(() => undefined);
          throw new Error(t('maintenanceActivities.uploadError'));
        }
      }
      close();
      await onSaved();
    } catch (err) {
      console.error('Failed to create maintenance activity:', err);
      if (errorBox) {
        errorBox.textContent = err instanceof ApiError ? err.message : (err instanceof Error ? err.message : t('maintenanceActivities.saveError'));
        errorBox.hidden = false;
      }
      submitBtn.disabled = false;
    }
  });
}

export function openEditAgreementDialog(agreement: RentalAgreementDto, onSaved: () => Promise<void>): void {
  const overlay = openOverlay(`
    <div class="modal">
      <div class="modal-header"><h2>${t('agreement.editTitle')}</h2><button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button></div>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="form-row"><div class="form-group"><label>${t('common.tenant')}</label><input class="form-input-static" type="text" value="${escapeHtml(agreement.tenantName)}" disabled></div><div class="form-group"><label>${t('common.property')}</label><input class="form-input-static" type="text" value="${escapeHtml(agreement.propertyAddress)}" disabled></div></div>
        <div class="form-group"><label>${t('common.rentAmountPerMonth')} *</label><input name="rentAmount" type="number" step="0.01" min="0" required value="${agreement.rentAmount ?? ''}"></div>
        <div class="form-row"><div class="form-group"><label>${t('common.startDate')} *</label><input name="startDate" type="date" required value="${agreement.startDate || ''}"></div><div class="form-group"><label>${t('common.endDate')}</label><input name="endDate" type="date" value="${agreement.endDate || ''}"></div></div>
        <div class="modal-actions"><button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button><button type="submit" class="btn btn-primary">${t('common.save')}</button></div>
      </form>
    </div>
  `, '200');
  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = overlay.querySelector('[type="submit"]') as HTMLButtonElement;
    clearDialogError(overlay);
    submitBtn.disabled = true;
    const fd = new FormData(event.target as HTMLFormElement);
    const data: UpdateRentalAgreementRequest = {
      tenantId: agreement.tenantId,
      propertyId: agreement.propertyId,
      rentAmount: parseFloat(fd.get('rentAmount') as string),
      startDate: fd.get('startDate') as string,
      endDate: (fd.get('endDate') as string) || undefined,
    };
    try {
      await rentalAgreementsApi.update(agreement.id, data);
      close();
      await onSaved();
    } catch (err) {
      console.error('Failed to update agreement:', err);
      showDialogError(overlay, err instanceof ApiError ? err.message : t('agreement.updateError'));
      submitBtn.disabled = false;
    }
  });
}

export function openRentChangeDialog(agreement: RentalAgreementDto, onSaved: () => Promise<void>): void {
  let step = 1;
  let stopDate = '';
  let newStartDate = '';
  let newRentAmount = '';
  let wizardError = '';
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.style.zIndex = '200';
  const modal = document.createElement('div');
  modal.className = 'modal';
  const dot = (n: number) => `<div style="width:2rem;height:2rem;border-radius:50%;background:${step >= n ? 'var(--primary,#6366f1)' : 'var(--border,#e2e8f0)'};color:${step >= n ? 'white' : 'var(--text-muted,#94a3b8)'};display:flex;align-items:center;justify-content:center;font-weight:600;font-size:0.875rem;flex-shrink:0;">${step > n ? '✓' : n}</div>`;
  const connector = (filled: boolean) => `<div style="flex:1;height:2px;background:${filled ? 'var(--primary,#6366f1)' : 'var(--border,#e2e8f0)'};margin-top:1rem;"></div>`;
  const stepLabel = (n: number, label: string) => `<div style="display:flex;flex-direction:column;align-items:center;gap:0.375rem;">${dot(n)}<span style="font-size:0.7rem;color:${step >= n ? 'var(--text,#0f172a)' : 'var(--text-muted,#94a3b8)'};text-align:center;line-height:1.3;white-space:nowrap;">${label}</span></div>`;
  const infoBox = (rows: [string, string][]) => `<div style="background:var(--surface,#f8fafc);border:1px solid var(--border,#e2e8f0);border-radius:0.5rem;padding:1rem;margin-bottom:1rem;"><div style="display:grid;grid-template-columns:auto 1fr;gap:0.4rem 1rem;font-size:0.875rem;">${rows.map(([label, value]) => `<span style="color:var(--text-muted,#64748b);">${label}</span><span>${value}</span>`).join('')}</div></div>`;
  const renderWizard = () => {
    const suggestedStart = stopDate ? (() => { const d = new Date(stopDate); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })() : '';
    modal.innerHTML = `
      <div class="modal-header"><h2>${t('agreement.rentChange.title')}</h2><button class="modal-close" data-action="close">${PROPERTY_ICONS.close}</button></div>
      <div style="display:flex;align-items:flex-start;padding:1.25rem 1.5rem 0.25rem;gap:0.5rem;">${stepLabel(1, t('agreement.rentChange.currentAgreementStep'))}${connector(step > 1)}${stepLabel(2, t('agreement.rentChange.newAgreementStep'))}${connector(step > 2)}${stepLabel(3, t('agreement.rentChange.confirmStep'))}</div>
      <div style="height:1px;background:var(--border,#e2e8f0);margin:0.75rem 0;"></div>
      <div style="padding:0 1.5rem 0.25rem;">
        ${wizardError ? `<div class="form-error">${escapeHtml(wizardError)}</div>` : ''}
        ${step === 1 ? `<p style="color:var(--text-muted,#64748b);font-size:0.875rem;margin-bottom:1rem;">${t('agreement.rentChange.closeCurrentHelp')}</p>${infoBox([[t('common.tenant'), escapeHtml(agreement.tenantName)], [t('common.property'), escapeHtml(agreement.propertyAddress)], [t('common.rentAmount'), formatMonthlyRent(agreement.rentAmount)], [t('common.startDate'), formatDate(agreement.startDate || '')]])}<div class="form-group"><label>${t('agreement.rentChange.currentEndDate')} *</label><input name="stopDate" type="date" required value="${stopDate}"></div>`
          : step === 2 ? `<p style="color:var(--text-muted,#64748b);font-size:0.875rem;margin-bottom:1rem;">${t('agreement.rentChange.setupNewHelp')}</p><div class="form-row"><div class="form-group"><label>${t('common.tenant')}</label><input type="text" value="${escapeHtml(agreement.tenantName)}" disabled style="background:var(--color-surface,#f8fafc);color:var(--color-text-muted,#64748b);"></div><div class="form-group"><label>${t('common.property')}</label><input type="text" value="${escapeHtml(agreement.propertyAddress)}" disabled style="background:var(--color-surface,#f8fafc);color:var(--color-text-muted,#64748b);"></div></div><div class="form-group"><label>${t('agreement.rentChange.newStartDate')} *</label><input name="newStartDate" type="date" required value="${newStartDate || suggestedStart}"></div><div class="form-group"><label>${t('agreement.rentChange.newRentAmount')} *</label><input name="newRentAmount" type="number" step="0.01" min="0" required value="${newRentAmount}" placeholder="${agreement.rentAmount?.toFixed(2) ?? ''}"></div>`
            : `<p style="color:var(--text-muted,#64748b);font-size:0.875rem;margin-bottom:1rem;">${t('agreement.rentChange.reviewHelp')}</p>${infoBox([[t('common.tenant'), escapeHtml(agreement.tenantName)], [t('common.property'), escapeHtml(agreement.propertyAddress)], [t('common.endDate'), formatDate(stopDate)]])}${infoBox([[t('common.tenant'), escapeHtml(agreement.tenantName)], [t('common.property'), escapeHtml(agreement.propertyAddress)], [t('common.rentAmount'), formatMonthlyRent(parseFloat(newRentAmount))], [t('common.startDate'), formatDate(newStartDate)]])}`}
      </div>
      <div class="modal-actions">${step > 1 ? `<button class="btn btn-outline" data-action="prev">${t('common.previous')}</button>` : `<span></span>`}<div style="display:flex;gap:0.5rem;"><button class="btn btn-outline" data-action="close">${t('common.cancel')}</button>${step < 3 ? `<button class="btn btn-primary" data-action="next">${t('common.next')}</button>` : `<button class="btn btn-primary" data-action="confirm">${t('common.confirm')}</button>`}</div></div>
    `;
    modal.querySelectorAll('[data-action="close"]').forEach((button) => button.addEventListener('click', () => overlay.remove()));
    modal.querySelector('[data-action="prev"]')?.addEventListener('click', () => { wizardError = ''; step -= 1; renderWizard(); });
    modal.querySelector('[data-action="next"]')?.addEventListener('click', () => {
      wizardError = '';
      if (step === 1) {
        const value = (modal.querySelector('[name="stopDate"]') as HTMLInputElement).value;
        if (!value) { (modal.querySelector('[name="stopDate"]') as HTMLInputElement).reportValidity(); return; }
        stopDate = value;
      } else if (step === 2) {
        const startDate = (modal.querySelector('[name="newStartDate"]') as HTMLInputElement).value;
        const rentAmount = (modal.querySelector('[name="newRentAmount"]') as HTMLInputElement).value;
        if (!startDate) { (modal.querySelector('[name="newStartDate"]') as HTMLInputElement).reportValidity(); return; }
        if (!rentAmount) { (modal.querySelector('[name="newRentAmount"]') as HTMLInputElement).reportValidity(); return; }
        newStartDate = startDate;
        newRentAmount = rentAmount;
      }
      step += 1;
      renderWizard();
    });
    modal.querySelector('[data-action="confirm"]')?.addEventListener('click', async () => {
      const btn = modal.querySelector('[data-action="confirm"]') as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = t('common.processing');
      wizardError = '';
      try {
        await rentalAgreementsApi.update(agreement.id, { tenantId: agreement.tenantId, propertyId: agreement.propertyId, rentAmount: agreement.rentAmount ?? 0, startDate: agreement.startDate ?? '', endDate: stopDate });
        await rentalAgreementsApi.create({ tenantId: agreement.tenantId, propertyId: agreement.propertyId, rentAmount: parseFloat(newRentAmount), startDate: newStartDate });
        overlay.remove();
        await onSaved();
      } catch (err) {
        console.error('Failed to apply rent change:', err);
        wizardError = err instanceof ApiError ? err.message : t('agreement.rentChange.error');
        renderWizard();
      }
    });
  };
  overlay.appendChild(modal);
  overlay.addEventListener('click', (event) => { if (event.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
  renderWizard();
}

export function openPropertyDossierExportDialog(propertyId: string, onBlob: (blob: Blob, fileName: string) => void): void {
  const overlay = openOverlay(`
    <div class="modal">
      <div class="modal-header"><h2>${t('dossier.property.modalTitle')}</h2><button class="modal-close" data-action="cancel">${PROPERTY_ICONS.close}</button></div>
      <p class="modal-subtitle">${t('dossier.property.modalDescription')}</p>
      <form>
        <div class="form-error" data-form-error hidden></div>
        <div class="dossier-options">
          <label class="dossier-option"><input type="checkbox" name="includePropertySummary" checked><span class="dossier-option-copy">${t('dossier.property.section.summary')}</span></label>
          <label class="dossier-option"><input type="checkbox" name="includeTenants" checked><span class="dossier-option-copy">${t('dossier.property.section.tenants')}</span></label>
          <label class="dossier-option"><input type="checkbox" name="includeDocuments" checked><span class="dossier-option-copy">${t('dossier.property.section.documents')}</span></label>
          <label class="dossier-option"><input type="checkbox" name="includeReminders" checked><span class="dossier-option-copy">${t('dossier.property.section.reminders')}</span></label>
        </div>
        <div class="modal-actions"><button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button><button type="submit" class="btn btn-primary">${t('common.export')}</button></div>
      </form>
    </div>
  `);
  const close = () => overlay.remove();
  overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((button) => button.addEventListener('click', close));
  overlay.querySelector('form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const submitBtn = form.querySelector('[type="submit"]') as HTMLButtonElement;
    const errorBox = form.querySelector<HTMLElement>('[data-form-error]');
    const data: ExportPropertyDossierRequest = {
      includePropertySummary: (form.elements.namedItem('includePropertySummary') as HTMLInputElement).checked,
      includeTenants: (form.elements.namedItem('includeTenants') as HTMLInputElement).checked,
      includeDocuments: (form.elements.namedItem('includeDocuments') as HTMLInputElement).checked,
      includeReminders: (form.elements.namedItem('includeReminders') as HTMLInputElement).checked,
    };
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    if (!Object.values(data).some(Boolean)) {
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = t('dossier.error.emptySelection');
      }
      return;
    }
    submitBtn.disabled = true;
    submitBtn.textContent = t('common.generating');
    try {
      const result = await dossiersApi.exportProperty(propertyId, data);
      onBlob(result.blob, result.fileName);
      close();
    } catch (error) {
      console.error('Failed to export property dossier:', error);
      submitBtn.disabled = false;
      submitBtn.textContent = t('common.export');
      showDialogError(form, error instanceof ApiError ? error.message : t('dossier.error.exportFailed'));
    }
  });
}

export async function deletePropertyDocument(documentId: string): Promise<boolean> {
  if (!await confirmDialog(t('document.confirmDelete'))) return false;
  await documentsApi.delete(documentId);
  return true;
}

export async function completePropertyReminder(reminder: RentalAgreementDto | any): Promise<void> {
  await remindersApi.update(reminder.id, { title: reminder.title, dueDate: reminder.dueDate, isCompleted: true });
}

export async function deletePropertyReminder(reminderId: string): Promise<boolean> {
  if (!await confirmDialog(t('reminder.confirmDelete'))) return false;
  await remindersApi.delete(reminderId);
  return true;
}

export async function deletePropertyMaintenanceActivity(activityId: string): Promise<boolean> {
  if (!await confirmDialog(t('maintenanceActivities.confirmDelete'))) return false;
  await maintenanceActivitiesApi.delete(activityId);
  return true;
}
